import { config } from '../config.js';
import { fetchAPI, stleaf, APIResponse, SpanTag } from '../helpers.js';

/** tags_v2_media_map.method value for user-drawn bounding-box labels. */
export const MANUAL_LABEL_METHOD = 'manual-label';

export interface AnnotationTag {
  id: string;
  colour?: string;
  geometry: any;
  name?: string;
  method?: string;
}

export interface AddAnnotationTagRequest {
  mediaId: string;
  geometry: any;
  colour?: string;
  name: string;
  method?: string;
}

export interface UpdateAnnotationTagRequest {
  name?: string;
  colour?: string;
  geometry?: any;
}

export interface AnnotationTagsResponse {
  success: boolean;
  annotations: AnnotationTag[];
}

export interface AddAnnotationTagResponse {
  success: boolean;
  annotationId: string;
}

export interface DeleteAnnotationTagResponse {
  success: boolean;
}

export interface DeleteBulkAnnotationTagsResponse {
  success: boolean;
  deletedCount: number;
}

export interface AnnotationLabelStat {
  tagId: string;
  name: string;
  annotationCount: number;
  mediaCount: number;
}

export interface GetLabelStatsResponse {
  success: boolean;
  labelClasses: AnnotationLabelStat[];
  mediaCount: number;
}

const headers = {
  'Content-Type': 'application/json',
};

/**
 * Add an AI annotation tag to a media item
 */
export const addAnnotationTag = (
  st: SpanTag,
  request: AddAnnotationTagRequest,
): Promise<APIResponse<AddAnnotationTagResponse>> => {
  const url = `${config.API_URL}/v2/annotation-tags`;
  return fetchAPI(stleaf(st, 'annotation-tags-add'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

/**
 * Current manual-label stats for the org (per label class + distinct media)
 */
export const getLabelStats = (
  st: SpanTag,
): Promise<APIResponse<GetLabelStatsResponse>> => {
  const url = `${config.API_URL}/v2/annotation-tags/label-stats`;
  return fetchAPI(stleaf(st, 'annotation-tags-label-stats'), url, {
    method: 'GET',
    credentials: 'include',
  });
};

/**
 * Get all annotation tags for a media item
 */
export const getAnnotationTags = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse<AnnotationTagsResponse>> => {
  const url = `${config.API_URL}/v2/annotation-tags/media/${mediaId}`;
  return fetchAPI(stleaf(st, 'annotation-tags-get'), url, {
    method: 'GET',
    credentials: 'include',
  });
};

/**
 * Update an annotation tag
 */
export const updateAnnotationTag = (
  st: SpanTag,
  annotationTagId: string,
  request: UpdateAnnotationTagRequest,
): Promise<APIResponse<{ success: boolean }>> => {
  const url = `${config.API_URL}/v2/annotation-tags/${annotationTagId}`;
  return fetchAPI(stleaf(st, 'annotation-tags-update'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(request),
  });
};

/**
 * Delete an AI annotation tag by ID
 */
export const deleteAnnotationTag = (
  st: SpanTag,
  annotationTagId: string,
): Promise<APIResponse<DeleteAnnotationTagResponse>> => {
  const url = `${config.API_URL}/v2/annotation-tags/${annotationTagId}`;
  return fetchAPI(stleaf(st, 'annotation-tags-delete'), url, {
    method: 'DELETE',
    credentials: 'include',
  });
};

/**
 * Delete an AI annotation tag for a specific media item
 */
export const deleteAnnotationTagByMedia = (
  st: SpanTag,
  mediaId: string,
  annotationTagId: string,
): Promise<APIResponse<DeleteAnnotationTagResponse>> => {
  const url = `${config.API_URL}/v2/annotation-tags/media/${mediaId}/${annotationTagId}`;
  return fetchAPI(stleaf(st, 'annotation-tags-delete-media'), url, {
    method: 'DELETE',
    credentials: 'include',
  });
};

/**
 * Delete multiple AI annotation tags by IDs
 */
export const deleteBulkAnnotationTags = (
  st: SpanTag,
  ids: string[],
): Promise<APIResponse<DeleteBulkAnnotationTagsResponse>> => {
  const url = `${config.API_URL}/v2/annotation-tags/bulk`;
  return fetchAPI(stleaf(st, 'antdbk'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: JSON.stringify({ ids }),
  });
};
